import type { UpdateMedicalAssistantApiPayload } from '@omecare/utils';
import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import http from '@/utils/http.service';

/**
 * Mutation to self-update the current medical assistant's profile via
 * `PATCH /assistants/me`. Invalidates the `['me']` query on success so the
 * profile sheet reflects the new values.
 *
 * @returns the react-query mutation result
 */
const useEditAssistantProfileMutation = (): UseMutationResult<
  Awaited<ReturnType<typeof http.assistants.updateMyProfile>>,
  Error,
  UpdateMedicalAssistantApiPayload
> => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: UpdateMedicalAssistantApiPayload) =>
      http.assistants.updateMyProfile(payload),
    onSuccess() {
      toast.success(t('http.edit-profile.success'));
      queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
};

export default useEditAssistantProfileMutation;
